import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { dateFmt } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { CheckSquare, Flag, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [null, "low", "medium", "high"];
const FLAG_COLOR = { low: "text-slate-400", medium: "text-amber-500", high: "text-red-500" };

export function TasksTab({ lead, users }) {
  const { can, me } = useApp();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const umap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["crm-tasks", lead.id], queryFn: () => get(`/crm/tasks?entity=lead&entity_id=${lead.id}`),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["crm-tasks", lead.id] });
  const create = useMutation({
    mutationFn: (body) => post("/crm/tasks", body),
    onSuccess: () => { invalidate(); setTitle(""); },
  });
  const update = useMutation({ mutationFn: ({ id, body }) => patch(`/crm/tasks/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id) => del(`/crm/tasks/${id}`), onSuccess: () => { invalidate(); toast.success("Task deleted"); } });

  const cyclePriority = (t) => {
    const idx = PRIORITIES.indexOf(t.priority || null);
    update.mutate({ id: t.id, body: { priority: PRIORITIES[(idx + 1) % PRIORITIES.length] } });
  };

  const submit = (e) => {
    if (e.key !== "Enter" || !title.trim()) return;
    create.mutate({ entity: "lead", entity_id: lead.id, title: title.trim(), owner_id: me?.id, done: false });
  };

  if (isLoading) return null;
  const sorted = [...tasks].sort((a, b) => (a.done === b.done ? 0 : a.done ? 1 : -1));

  return (
    <div className="space-y-1">
      {sorted.length === 0 && <EmptyState icon={CheckSquare} title="No tasks yet" subtitle="Add one below." />}
      {sorted.map((t) => (
        <div key={t.id} data-testid={`task-row-${t.id}`} className="group flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
          <Checkbox checked={t.done} data-testid={`task-check-${t.id}`}
            onCheckedChange={(v) => update.mutate({ id: t.id, body: { done: !!v } })} />
          <span className={cn("flex-1 text-sm", t.done && "text-muted-foreground line-through")}>{t.title}</span>
          {t.due && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{dateFmt(t.due)}</span>}
          <button onClick={() => cyclePriority(t)} title="Cycle priority" data-testid={`task-priority-${t.id}`}>
            <Flag className={cn("h-3.5 w-3.5", t.priority ? FLAG_COLOR[t.priority] : "text-muted-foreground/30")} />
          </button>
          <Avatar user={umap[t.owner_id]} size="xs" />
          {can("crm", "delete") && (
            <button data-testid={`del-task-${t.id}`} onClick={() => remove.mutate(t.id)}
              className="hidden rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600 group-hover:block">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {can("crm", "create") && (
        <input data-testid="new-task-input" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={submit}
          placeholder="+ Add task and press Enter"
          className="mt-1 w-full rounded-lg border border-dashed border-border bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground" />
      )}
    </div>
  );
}
